import type { GrowthArea, GrowthVenue } from '@pulsify/shared/dto'
import { MapPinOff, Target, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { useAdminGrowthOpportunities } from '~/entities/stats/model/use-admin-stats'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Card, CardContent } from '~/shared/ui/card'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'

const WINDOWS = [7, 30, 90] as const
type Window = (typeof WINDOWS)[number]

const WINDOW_LABELS: Record<Window, () => string> = {
  7: m.geoWindow7,
  30: m.geoWindow30,
  90: m.geoWindow90,
}

type Move = 'supply' | 'grow' | 'activate' | 'hold'

const MOVE_LABELS: Record<Move, () => string> = {
  supply: m.opportunitiesActionSupply,
  grow: m.opportunitiesActionGrow,
  activate: m.opportunitiesActionActivate,
  hold: m.opportunitiesActionHold,
}

/** Reads the area's own numbers rather than its rank, so the advice survives re-sorting. */
function moveFor(area: GrowthArea): Move {
  const supply = area.events + area.listings
  const demand = area.people + area.scans
  if (demand > 0 && supply === 0) return 'supply'
  if (demand === 0 && supply > 0) return 'activate'
  if (demand >= supply * 3) return 'grow'
  return 'hold'
}

const MOVE_TONE: Record<Move, string> = {
  supply: 'bg-brand text-brand-foreground',
  grow: 'bg-brand-subtle text-brand-subtle-foreground',
  activate: 'bg-muted text-muted-foreground',
  hold: 'bg-muted text-muted-foreground',
}

function AreaRow({ area, index }: { area: GrowthArea; index: number }) {
  const locale = getLocale()
  const count = (value: number) => new Intl.NumberFormat(locale).format(value)
  const move = moveFor(area)
  return (
    <tr className="border-border border-b last:border-0">
      <td className="py-3 pr-4 pl-4 text-muted-foreground text-xs tabular-nums">{index + 1}</td>
      <td className="py-3 pr-4">
        {area.nearestPlace ? (
          <>
            <span className="font-medium">{area.nearestPlace}</span>
            {area.nearestPlaceKm !== null && area.nearestPlaceKm > 1 ? (
              <span className="block text-muted-foreground text-xs">
                {m.opportunitiesNearPlace({
                  km: area.nearestPlaceKm.toFixed(1),
                  place: area.nearestPlace,
                })}
              </span>
            ) : null}
          </>
        ) : (
          <span className="inline-flex items-center gap-1.5 font-medium">
            <MapPinOff className="w-3.5 h-3.5 text-muted-foreground" aria-hidden />
            {m.opportunitiesUnnamed()}
          </span>
        )}
      </td>
      <td className="py-3 pr-4 tabular-nums">{count(area.people)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(area.scans)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(area.events + area.listings)}</td>
      <td className="py-3 pr-4 font-medium tabular-nums">{area.opportunityScore.toFixed(2)}</td>
      <td className="py-3 pr-4">
        <span className={cn('inline-block px-2 py-1 rounded-md text-xs', MOVE_TONE[move])}>
          {MOVE_LABELS[move]()}
        </span>
      </td>
    </tr>
  )
}

function VenueRow({ venue }: { venue: GrowthVenue }) {
  const locale = getLocale()
  const count = (value: number) => new Intl.NumberFormat(locale).format(value)
  const rate = venue.scanToAccount
  return (
    <tr className="border-border border-b last:border-0">
      <td className="py-3 pr-4 pl-4 font-medium">{venue.title}</td>
      <td className="py-3 pr-4 text-muted-foreground">{venue.city || '-'}</td>
      <td className="py-3 pr-4 tabular-nums">{count(venue.scans)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(venue.joins)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(venue.accounts)}</td>
      <td className="py-3 pr-4">
        {rate === null ? (
          <span className="text-muted-foreground">-</span>
        ) : (
          <Badge variant={rate >= 0.2 ? 'default' : 'secondary'} className="tabular-nums">
            {new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
              rate,
            )}
          </Badge>
        )}
      </td>
    </tr>
  )
}

function TableCard({
  title,
  icon: Icon,
  head,
  empty,
  caption,
  loading,
  rows,
  children,
}: {
  title: string
  icon: typeof Target
  head: readonly string[]
  empty: string
  caption?: string
  loading: boolean
  rows: number
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 font-medium text-lg">
        <Icon className="w-5 h-5" aria-hidden />
        {title}
      </h2>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="space-y-3 p-6">
              {['a', 'b', 'c', 'd', 'e'].map((placeholder) => (
                <Skeleton key={placeholder} className="w-full h-9" />
              ))}
            </div>
          ) : rows === 0 ? (
            <p className="p-10 text-muted-foreground text-sm text-center">{empty}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-border border-b text-muted-foreground text-xs text-left uppercase tracking-wide">
                  {head.map((label, index) => (
                    <th key={label} className={cn('py-3 pr-4 font-medium', index === 0 && 'pl-4')}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{children}</tbody>
            </table>
          )}
        </CardContent>
      </Card>
      {caption ? <p className="text-muted-foreground text-xs">{caption}</p> : null}
    </section>
  )
}

export function OpportunityBoard() {
  const [days, setDays] = useState<Window>(30)
  const query = useAdminGrowthOpportunities({ days })
  const locale = getLocale()

  const areas = query.data?.areas ?? []
  const venues = query.data?.venues ?? []
  const loading = !query.data

  return (
    <div className="px-4 sm:px-8 py-8 sm:py-16">
      <div className="space-y-10 mx-auto max-w-6xl">
        <header className="flex flex-wrap justify-between items-end gap-x-6 gap-y-4">
          <div className="space-y-1.5">
            <h1 className="flex items-center gap-2.5 font-semibold text-foreground text-3xl tracking-tight">
              <TrendingUp className="w-7 h-7" aria-hidden />
              {m.opportunitiesTitle()}
            </h1>
            <p className="text-muted-foreground text-sm">{m.opportunitiesSubtitle()}</p>
          </div>
          <SegmentedControl
            label={m.geoWindowLabel()}
            value={String(days)}
            onChange={(value) => setDays(Number(value) as Window)}
            options={WINDOWS.map((window) => ({
              value: String(window),
              label: WINDOW_LABELS[window](),
            }))}
          />
        </header>

        <TableCard
          title={m.opportunitiesAreas()}
          icon={Target}
          loading={loading}
          rows={areas.length}
          empty={m.opportunitiesEmptyAreas()}
          caption={
            query.data
              ? m.opportunitiesAreaCaption({
                  km: new Intl.NumberFormat(locale).format(query.data.cellKm),
                })
              : undefined
          }
          head={[
            '#',
            m.opportunitiesColumnPlace(),
            m.opportunitiesColumnDemand(),
            m.opportunitiesColumnScans(),
            m.opportunitiesColumnSupply(),
            m.opportunitiesColumnScore(),
            m.opportunitiesColumnAction(),
          ]}
        >
          {areas.map((area, index) => (
            <AreaRow key={`${area.lat}|${area.lng}`} area={area} index={index} />
          ))}
        </TableCard>

        <TableCard
          title={m.opportunitiesVenues()}
          icon={Target}
          loading={loading}
          rows={venues.length}
          empty={m.opportunitiesEmptyVenues()}
          head={[
            m.opportunitiesColumnVenue(),
            m.opportunitiesColumnCity(),
            m.opportunitiesColumnScans(),
            m.opportunitiesColumnJoins(),
            m.opportunitiesColumnAccounts(),
            m.opportunitiesColumnConversion(),
          ]}
        >
          {venues.map((venue) => (
            <VenueRow key={venue.eventId} venue={venue} />
          ))}
        </TableCard>
      </div>
    </div>
  )
}
